/**
 * Content View Tracking Hook
 * 
 * Tracks when a user has meaningfully viewed content (posts, comments, etc.)
 * 
 * A post/content is considered "viewed" ONLY if:
 * - Time on page > 10 seconds
 * - Scroll depth > 60%
 * 
 * This hook should be used on full-page content views (not feed items).
 * For feed items, use useTrackEntityVisibility instead.
 */

import { useEffect, useRef, useState } from 'react';
import { entity_read } from './events';

interface UseTrackContentViewOptions {
  contentType: 'post' | 'comment' | 'article' | 'question' | 'poll';
  contentId: string;
  minTimeSeconds?: number;
  minScrollDepth?: number;
  disabled?: boolean;
}

interface ViewMetrics {
  timeOnPage: number;
  maxScrollDepth: number;
  hasViewed: boolean;
}

export function useTrackContentView(options: UseTrackContentViewOptions) {
  const {
    contentType,
    contentId,
    minTimeSeconds = 10,
    minScrollDepth = 0.6,
    disabled = false,
  } = options;

  const [metrics, setMetrics] = useState<ViewMetrics>({
    timeOnPage: 0,
    maxScrollDepth: 0,
    hasViewed: false,
  });

  const startTimeRef = useRef<number>(Date.now());
  const hasTrackedRef = useRef<boolean>(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (disabled || typeof window === 'undefined') return;

    startTimeRef.current = Date.now();
    hasTrackedRef.current = false;

    // Track scroll depth
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      const scrollDepth = (scrollTop + windowHeight) / documentHeight;

      setMetrics(prev => ({
        ...prev,
        maxScrollDepth: Math.max(prev.maxScrollDepth, scrollDepth),
      }));
    };

    // Track time on page
    intervalRef.current = setInterval(() => {
      const timeOnPage = (Date.now() - startTimeRef.current) / 1000;

      setMetrics(prev => {
        const newMetrics = { ...prev, timeOnPage };

        // Check if view criteria are met
        if (
          !hasTrackedRef.current &&
          timeOnPage >= minTimeSeconds &&
          prev.maxScrollDepth >= minScrollDepth
        ) {
          hasTrackedRef.current = true;

          entity_read({
            entity_id: contentId,
            entity_type: contentType,
            engagement_view_duration: Math.round(timeOnPage),
            engagement_scroll_depth: Math.round(prev.maxScrollDepth * 100),
            engagement_scrolled_to_end: prev.maxScrollDepth >= 0.9,
          });

          newMetrics.hasViewed = true;
        }

        return newMetrics;
      });
    }, 1000); // Check every second

    // Initial scroll check
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [contentType, contentId, minTimeSeconds, minScrollDepth, disabled]);

  return metrics;
}

/**
 * Example usage:
 * 
 * function PostPage({ postId }) {
 *   const viewMetrics = useTrackContentView({
 *     contentType: 'post',
 *     contentId: postId,
 *     minTimeSeconds: 10,
 *     minScrollDepth: 0.6,
 *   });
 *   
 *   return (
 *     <div>
 *       <h1>Post Content</h1>
 *       {viewMetrics.hasViewed && <span>✓ Tracked as viewed</span>}
 *     </div>
 *   );
 * }
 */
